import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Phone, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { T } from '@/constants/testIds';
import OtpInput from '@/components/OtpInput';
import CountrySelect from '@/components/CountrySelect';
import { Flag } from '@/components/Flag';
import { COUNTRY } from '@/lib/format';

// In production builds the dev-convenience pre-filled email is removed.
const DEFAULT_EMAIL = process.env.NODE_ENV === 'production' ? '' : 'alex.mwangi@gmail.com';

/**
 * Standalone tab pill renderer. Hoisted out of `LoginSender` so React
 * doesn't tear down and rebuild the subtree on every parent render
 * (`react/no-unstable-nested-components`).
 */
function TabBtn({ id, icon: Icon, label, tid, active, onSelect }) {
    return (
        <button
            data-testid={tid}
            onClick={() => onSelect(id)}
            style={{
                flex: 1, padding: '12px 14px', borderRadius: 12,
                background: active ? 'var(--nia-paper)' : 'transparent',
                color: active ? 'var(--nia-ink)' : 'var(--nia-text-muted)',
                border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center',
                boxShadow: active ? '0 4px 12px rgba(20, 17, 15, 0.06)' : 'none',
                transition: 'background-color 0.18s ease, color 0.18s ease',
                letterSpacing: '-0.005em',
            }}
        >
            <Icon size={16} /> {label}
        </button>
    );
}

export default function LoginSender() {
    const nav = useNavigate();
    const { login } = useAuth();
    const [tab, setTab] = useState('email'); // email | phone
    const [step, setStep] = useState('input'); // input | otp
    const [email, setEmail] = useState(DEFAULT_EMAIL);
    const [phone, setPhone] = useState('');
    const [country, setCountry] = useState('CA');
    const [otp, setOtp] = useState('');
    const [demoCode, setDemoCode] = useState('');
    const [delivered, setDelivered] = useState(false);
    const [fallbackReason, setFallbackReason] = useState('');
    const [busy, setBusy] = useState(false);

    const sendOtp = async () => {
        setBusy(true);
        try {
            const res = tab === 'email'
                ? await api.sendEmailOtp(email.trim())
                : await api.sendPhoneOtp(phone.trim(), country, 'sender');
            setDemoCode(res.data.demo_code || '');
            setDelivered(res.data.delivered === true);
            setFallbackReason(res.data.fallback_reason || '');
            setStep('otp');
            if (res.data.delivered) {
                toast.success('Verification code sent', { description: tab === 'email' ? `Check your inbox at ${email}` : `We sent it to ${COUNTRY[country].name} ${phone}` });
            } else {
                toast.message('Beta demo code shown below', { description: tab === 'phone' ? 'Twilio trial restricts unverified numbers. Demo code displayed.' : 'Email provider not configured. Demo code displayed.' });
            }
        } catch (e) {
            toast.error(extractErrorMessage(e, 'Could not send code'));
        } finally {
            setBusy(false);
        }
    };

    const verify = async () => {
        setBusy(true);
        try {
            const payload = tab === 'email'
                ? { channel: 'email', identifier: email.trim(), code: otp, role: 'sender' }
                : { channel: 'phone', identifier: phone.trim(), code: otp, role: 'sender', country };
            const r = await api.verifyOtp(payload);
            await login(r.data.token, r.data.user, r.data.csrf_token);
            toast.success(`Welcome back, ${r.data.user.name.split(' ')[0]}`);
            const ack = (() => { try { return localStorage.getItem('nia_disclosure_v1') === '1'; } catch (e) { return false; } })();
            const home = ack ? '/sender/home' : '/welcome';
            nav(r.data.user.onboarded === false ? '/setup' : home, { replace: true });
        } catch (e) {
            toast.error(extractErrorMessage(e, 'Could not verify code'));
        } finally {
            setBusy(false);
        }
    };

    const handleTabSelect = (id) => { setTab(id); setStep('input'); setOtp(''); };

    return (
        <div className="nia-page">
            <div className="mobile-shell">
                <header style={{ padding: '14px 16px 6px', display: 'flex', alignItems: 'center', gap: 12, minHeight: 60 }}>
                    <Link to="/" aria-label="Back" style={{
                        width: 44, height: 44, minWidth: 44, minHeight: 44, borderRadius: 999,
                        border: '1px solid var(--nia-border)', background: 'var(--nia-paper)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: 'var(--nia-ink)',
                        flexShrink: 0,
                    }}>
                        <ArrowLeft size={20} />
                    </Link>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: 'var(--nia-text-subtle)', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sender</div>
                        <h1 className="font-display" style={{ fontSize: 24, fontWeight: 700, margin: '2px 0 0', letterSpacing: '-0.02em', color: 'var(--nia-ink)', lineHeight: 1.15 }}>Welcome back</h1>
                    </div>
                </header>

                <div style={{ padding: '18px 18px 28px' }}>
                    {step === 'input' && (
                        <>
                            <div style={{
                                background: 'var(--nia-muted)', borderRadius: 14, padding: 4,
                                display: 'flex', gap: 4, marginBottom: 18,
                            }}>
                                <TabBtn id="email" icon={Mail} label="Email" tid={T.senderEmailTab} active={tab === 'email'} onSelect={handleTabSelect} />
                                <TabBtn id="phone" icon={Phone} label="Phone" tid={T.senderPhoneTab} active={tab === 'phone'} onSelect={handleTabSelect} />
                            </div>

                            {tab === 'email' ? (
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--nia-text-muted)', display: 'block', marginBottom: 8, letterSpacing: '0.02em' }}>Email address</label>
                                    <input data-testid={T.senderEmailInput} type="email" className="nia-input" placeholder="you@example.com"
                                        value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                                </div>
                            ) : (
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--nia-text-muted)', display: 'block', marginBottom: 8, letterSpacing: '0.02em' }}>Country</label>
                                    <CountrySelect value={country} onChange={setCountry} options={['CA', 'GB', 'US', 'KE', 'UG']} testId={T.senderCountrySelect} />
                                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--nia-text-muted)', display: 'block', marginBottom: 8, marginTop: 14, letterSpacing: '0.02em' }}>Phone number</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: 18, top: 16, color: 'var(--nia-text-muted)', fontWeight: 600 }}>{COUNTRY[country].dial}</span>
                                        <input data-testid={T.senderPhoneInput} className="nia-input" style={{ paddingLeft: 60 }} placeholder="7XXXXXXXXX"
                                            inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} />
                                    </div>
                                </div>
                            )}

                            <button data-testid={T.senderSendOtpBtn} className="nia-btn-primary" style={{ marginTop: 24 }} onClick={sendOtp} disabled={busy || (tab === 'email' ? !email : !phone)}>
                                {busy ? 'Sending…' : 'Send verification code'}
                            </button>

                            <p style={{ marginTop: 16, fontSize: 12, color: 'var(--nia-text-subtle)', textAlign: 'center', lineHeight: 1.5 }}>
                                By continuing, you agree to Nia&rsquo;s <span style={{ color: 'var(--nia-clay)', fontWeight: 600 }}>Terms</span> and <span style={{ color: 'var(--nia-clay)', fontWeight: 600 }}>Privacy</span>.
                            </p>
                        </>
                    )}

                    {step === 'otp' && (
                        <div className="fade-in">
                            <p style={{ color: 'var(--nia-text-muted)', fontSize: 14, margin: '4px 0 18px', lineHeight: 1.5 }}>
                                Enter the 6-digit code we sent to {tab === 'email' ? <b style={{ color: 'var(--nia-ink)' }}>{email}</b> : <b style={{ color: 'var(--nia-ink)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Flag code={country} size={16} /> {phone}</b>}.
                            </p>
                            <OtpInput value={otp} onChange={setOtp} testId={T.senderOtpInput} />
                            {delivered && !demoCode && (
                                <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--nia-sage-tint)', borderRadius: 12, fontSize: 12, color: 'var(--nia-sage)', fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--nia-sage)' }} />
                                    Code delivered to your {tab === 'email' ? 'inbox' : 'phone'}.
                                </div>
                            )}
                            {demoCode && (
                                <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--nia-gold-tint)', borderRadius: 12, fontSize: 12, color: '#6B5800', fontWeight: 600 }}>
                                    <b>Beta demo:</b> Your code is <span style={{ fontFamily: "'Roboto Mono', monospace", letterSpacing: 1, fontWeight: 700 }}>{demoCode}</span>
                                    {fallbackReason && <div style={{ fontSize: 10, opacity: 0.75, marginTop: 4 }}>Reason: {fallbackReason}</div>}
                                </div>
                            )}
                            <button data-testid={T.senderVerifyBtn} className="nia-btn-primary" style={{ marginTop: 24 }} disabled={busy || otp.length !== 6} onClick={verify}>
                                {busy ? 'Verifying…' : 'Verify and continue'}
                            </button>
                            <button onClick={() => { setStep('input'); setOtp(''); }} style={{ marginTop: 12, background: 'transparent', border: 'none', color: 'var(--nia-text-muted)', fontWeight: 600, cursor: 'pointer', width: '100%' }}>
                                Use a different {tab === 'email' ? 'email' : 'number'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
